/**
 * Stops the benign "ResizeObserver loop …" browser notice from tripping the dev
 * error overlay.
 *
 * The notice fires when a ResizeObserver callback nudges layout the browser
 * can't flush in the same frame (e.g. the tab bar's overflow measurement on tab
 * switch). It has no functional effect, but the dev overlay surfaces it as a
 * fatal runtime error and covers the page. We swallow only the two exact
 * ResizeObserver-loop messages — every other error still propagates.
 *
 * This does NOT stop `bun dev` from printing the notice to the terminal as
 * `error: "ResizeObserver loop completed with undelivered notifications."`.
 * Bun's HMR client registers its own window `error` listener (which POSTs to
 * `/_bun/report_error`) and its runtime is injected ahead of app code, so it is
 * earlier in the listener list — and for an event dispatched *at* window, order
 * of registration wins over the capture flag, so `stopImmediatePropagation`
 * here comes too late. The terminal line is noise; the fix for a new one is to
 * stop the loop at its source rather than to catch it here.
 */
const RESIZE_OBSERVER_LOOP =
  /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/

export function silenceResizeObserverLoopError() {
  if (typeof window === 'undefined') return

  window.addEventListener(
    'error',
    (event) => {
      if (
        typeof event.message === 'string' &&
        RESIZE_OBSERVER_LOOP.test(event.message)
      ) {
        // Capture phase + stopImmediatePropagation prevents the dev overlay's
        // own window error listener from firing for this benign message.
        event.stopImmediatePropagation()
        event.preventDefault()
      }
    },
    true
  )
}
